'use client'

/**
 * Chatbot Widget Component
 *
 * Floating chat button that opens the chat window.
 * Renders on all tenant booking websites.
 */

import { useState, useEffect } from 'react'
import { MessageCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChatWindow } from './ChatWindow'
import type { ChatbotWidgetProps } from './types'

export function ChatbotWidget({
  tenantId,
  tenantName,
  tenantLogo,
  propertyId,
  propertyName,
  className,
}: ChatbotWidgetProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  // Avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  if (!isMounted) {
    return null
  }

  return (
    <>
      {/* Floating Chat Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full shadow-lg transition-all duration-200',
          'hover:scale-105 active:scale-95',
          'lg:bottom-6 sm:right-6',
          isOpen && 'opacity-0 pointer-events-none',
          className
        )}
        size="icon"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>

      {/* Chat Window */}
      <ChatWindow
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        tenantId={tenantId}
        tenantName={tenantName}
        tenantLogo={tenantLogo}
        propertyId={propertyId}
        propertyName={propertyName}
      />

      {/* Backdrop for mobile (optional - uncomment if desired) */}
      {/* {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 sm:hidden"
          onClick={() => setIsOpen(false)}
        />
      )} */}
    </>
  )
}
